import React, { useState } from 'react'
import { ViewStyle } from 'react-native'
import { Redirect, useHistory } from 'react-router'
import { observer } from 'mobx-react-lite'
import { DomainRounded, Search } from '@mui/icons-material'
import { InputAdornment, TextField } from '@mui/material'
import { useStores } from '@trivie/core'
import { color } from '@trivie/ui-web/theme/color'
import { Button, Text } from '@trivie/ui-mobile'
import { HourlyWarning } from '@trivie/ui-web/components/hourly-warning'

export const WorkspaceScreen: React.FunctionComponent = observer(() => {
  const { userStore } = useStores()
  const { instance, loginWorkspace } = userStore

  const history = useHistory()

  const [selectedWorkspace, setSelectedWorkspace] = useState<any>({})
  const [showHourly, setShowHourly] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const login = async () => {
    // move to after hourly warning prompt
    if (selectedWorkspace.has_sso_config) {
      userStore.setSSOWorkspace(selectedWorkspace)
      const ssoRedirect = await userStore?.getAccessCodeDetails(selectedWorkspace.access_code)
      if (ssoRedirect) {
        window.location.replace(ssoRedirect)
      }
    } else if (selectedWorkspace?.hourly_warning) {
      setShowHourly(true)
    } else {
      loginWorkspace(instance.token, selectedWorkspace.access_code, () =>
        history.replace('/library'),
      )
    }
  }

  const acceptTerms = async () => {
    setShowHourly(false)
    if (selectedWorkspace && instance) {
      userStore
        .handleTerms('accept', instance.token, selectedWorkspace.access_code)
        .then(async (data) => {
          if (data) {
            if (selectedWorkspace.has_sso_config) {
              userStore.setSSOWorkspace(selectedWorkspace)
              const ssoRedirect = await userStore?.getAccessCodeDetails(
                selectedWorkspace.access_code,
              )
              if (ssoRedirect) {
                window.location.replace(ssoRedirect)
              }
            } else {
              loginWorkspace(instance.token, selectedWorkspace.access_code, () =>
                history.replace('/library'),
              )
            }
          }
        })
    }
  }

  const rejectTerms = () => {
    setShowHourly(false)
    if (selectedWorkspace && instance) {
      userStore.handleTerms('reject', instance.token, selectedWorkspace.access_code).then(() => {
        userStore?.logout()
        history.replace('/login')
      })
    }
  }

  return instance ? (
    <div style={ROOT}>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
        <div style={WORKSPACE_LIST}>
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 60 }}>
            <Text
              style={{ marginBottom: 24 }}
              variant="headline"
              tx="Onboarding.Login.selectWorkspace"
            />
          </div>
          <div
            style={{
              ...LIST_CONTAINER,
              height: `${76 * instance.workspaces?.length + 100}px`,
              maxHeight: `calc(100vh - 440px)`,
            }}
          >
            <TextField
              placeholder="Search Workspaces"
              style={{ marginBottom: 16, paddingRight: 20 }}
              variant="standard"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment style={{ backgroundColor: '#fff' }} position="start">
                    <Search htmlColor={color.shade70} />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <ul style={LIST}>
              {instance.workspaces
                ?.filter((workspace) =>
                  workspace.company_name?.toLowerCase().includes(searchTerm.toLowerCase()),
                )
                .sort((a, b) => a.company_name.localeCompare(b.company_name))
                .map((workspace: any) => {
                  return (
                    <li
                      key={`${workspace.access_code}`}
                      onClick={() => setSelectedWorkspace(workspace)}
                      style={{
                        ...LIST_ITEM,
                        backgroundColor:
                          selectedWorkspace.access_code === workspace.access_code
                            ? color.blue
                            : '#F8FAFD',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <DomainRounded
                          style={{
                            marginRight: 20,
                            color:
                              selectedWorkspace.access_code === workspace.access_code
                                ? color.white
                                : '#4F5E74',
                          }}
                        />
                        <Text
                          variant="body1Emphasized"
                          style={{
                            color:
                              selectedWorkspace.access_code === workspace.access_code
                                ? color.white
                                : color.black,
                          }}
                          text={workspace.company_name}
                        />
                      </div>
                      {workspace.logo_path && (
                        <img src={workspace.logo_path} height={32} alt="company logo" />
                      )}
                    </li>
                  )
                })}
            </ul>
          </div>
        </div>
      </div>
      <Button
        accessibilityRole="button"
        accessibilityLabel="Continue"
        disabled={!selectedWorkspace.access_code}
        textStyle={{ color: color.palette.white }}
        variant="secondary"
        style={SUBMIT}
        tx="Common.continue"
        onPress={() => login()}
      />
      {showHourly && (
        <HourlyWarning
          accept={acceptTerms}
          reject={rejectTerms}
          message={selectedWorkspace?.hourly_warning}
        />
      )}
    </div>
  ) : (
    <Redirect to="/login" />
  )
})

const ROOT: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
}
const SUBMIT: ViewStyle = {
  width: 335,
  alignSelf: 'center',
}
const WORKSPACE_LIST: React.CSSProperties = {
  overflowY: 'auto',
  padding: 12,
}

const LIST: React.CSSProperties = {
  overflowY: 'auto',
  paddingRight: 20,
  listStyle: 'none',
  paddingLeft: 0,
}
const LIST_ITEM: React.CSSProperties = {
  cursor: 'pointer',
  width: '100%',
  padding: 20,
  paddingTop: 18,
  paddingBottom: 18,
  height: 60,
  borderRadius: 6,
  flexDirection: 'row',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
}

const LIST_CONTAINER: React.CSSProperties = {
  maxHeight: 'calc(100vh - 440px)',
  height: '100%',
  width: 632,
  display: 'flex',
  flexDirection: 'column',
}
